import { useEffect, useState } from "react";
import { Button, Checkbox, Divider, Popover, notification } from "antd";
import { BugOutlined, DownloadOutlined, SettingOutlined } from "@ant-design/icons";
import { AdminPanelModal } from "./AdminPanelModal";
import { checkWidgetContract } from "../widgetContractCheck";
import { useAuth0 } from "@auth0/auth0-react";
import {
  isLocalhost,
  isInternalUser,
  getDebugFlags,
  setDebugFlags,
  DEBUG_FLAG_LABELS,
  type DebugFlags,
} from "./debugFeatures";
import { useDashboard } from "../Contexts/dashboardHooks";
import { serializeDashboardSpec } from "../Contexts/serializeSpec";

export function DebugFeaturesButton() {
  const [flags, setFlags] = useState(getDebugFlags);
  const [adminOpen, setAdminOpen] = useState(false);
  const { state } = useDashboard();
  const { user } = useAuth0();

  // Dev/admin-only: on load, warn if any widget definition is missing a contract
  // handler or its schema changed (stale hash). See Docs/WIDGET_CONTRACT_DESIGN.md.
  useEffect(() => {
    if (!isLocalhost() && !isInternalUser(user?.email)) return;
    const problems = checkWidgetContract();
    if (problems.length) {
      notification.warning({
        message: "Widget contract incomplete",
        description: problems.map((p) => `${p.type}: ${p.problems.join("; ")}`).join(" · "),
        duration: 0,
        placement: "bottomRight",
      });
    }
  }, [user?.email]);

  if (!isLocalhost() && !isInternalUser(user?.email)) return null;

  const toggle = (key: keyof DebugFlags) => {
    const next = { ...flags, [key]: !flags[key] };
    setDebugFlags(next);
    setFlags(next);
  };

  const handleExportFull = () => {
    const spec = serializeDashboardSpec(state);
    const payload = { ...spec, name: state.name };
    const json = JSON.stringify(payload, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${state.name || "dashboard"}-full.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const content = (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 200 }}>
      {(Object.keys(DEBUG_FLAG_LABELS) as (keyof DebugFlags)[]).map((key) => (
        <Checkbox key={key} checked={flags[key]} onChange={() => toggle(key)}>
          {DEBUG_FLAG_LABELS[key]}
        </Checkbox>
      ))}
      <Divider style={{ margin: "6px 0" }} />
      <Button
        type="text"
        icon={<DownloadOutlined />}
        onClick={handleExportFull}
        style={{ textAlign: "left" }}
        block
      >
        Export Full Dashboard
      </Button>
      <Button
        type="text"
        icon={<SettingOutlined />}
        onClick={() => setAdminOpen(true)}
        style={{ textAlign: "left" }}
        block
      >
        Admin Panel
      </Button>
    </div>
  );

  return (
    <>
      <Popover content={content} title="Debug Features" trigger="click" placement="bottomRight">
        <Button
          icon={<BugOutlined />}
          size="small"
          style={{ fontSize: 11, color: "#888", borderColor: "#d9d9d9" }}
        >
          Debug
        </Button>
      </Popover>
      <AdminPanelModal open={adminOpen} onClose={() => setAdminOpen(false)} />
    </>
  );
}
